import { clsx } from "clsx";
import { CircleAlert, CircleCheck, Info, TriangleAlert } from "lucide-react";
import { m } from "motion/react";
import {
  useId,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from "react";

import styles from "./Primitives.module.css";

// Panel ------------------------------------------------------------------------------------

type PanelProps = HTMLAttributes<HTMLElement> & {
  as?: "section" | "div" | "article" | "aside";
  padding?: "sm" | "md" | "lg";
  glow?: boolean;
};

/** Frosted glass surface over the backdrop: blur, a top highlight and a soft shadow. */
export function Panel({ as = "section", padding = "md", glow, className, ...props }: PanelProps) {
  const Tag = as;
  return (
    <Tag
      className={clsx(styles.panel, styles[`pad-${padding}`], glow && styles.glow, className)}
      {...props}
    />
  );
}

// Alert ------------------------------------------------------------------------------------

type Tone = "info" | "success" | "warning" | "danger";
const alertIcons: Record<Tone, ReactNode> = {
  info: <Info size={18} />,
  success: <CircleCheck size={18} />,
  warning: <TriangleAlert size={18} />,
  danger: <CircleAlert size={18} />,
};

export function Alert({
  tone = "info",
  title,
  children,
  action,
  className,
}: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <m.div
      role={tone === "danger" ? "alert" : "status"}
      className={clsx(styles.alert, styles[`alert-${tone}`], className)}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <span className={styles.alertIcon} aria-hidden="true">
        {alertIcons[tone]}
      </span>
      <div className={styles.alertBody}>
        {title ? <p className={styles.alertTitle}>{title}</p> : null}
        {children ? <div className={styles.alertText}>{children}</div> : null}
      </div>
      {action ? <div className={styles.alertAction}>{action}</div> : null}
    </m.div>
  );
}

// Pills and chips --------------------------------------------------------------------------

export type PillTone = "neutral" | "info" | "success" | "warning" | "danger" | "live" | "brand";

export function StatusPill({
  tone = "neutral",
  children,
  className,
}: {
  tone?: PillTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={clsx(styles.pill, styles[`pill-${tone}`], className)}>
      <span className={styles.dot} aria-hidden="true" />
      {children}
    </span>
  );
}

export function Chip({
  children,
  icon,
  className,
  title,
}: {
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <span className={clsx(styles.chip, className)} title={title}>
      {icon ? <span aria-hidden="true">{icon}</span> : null}
      {children}
    </span>
  );
}

// Segmented control -------------------------------------------------------------------------

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  size = "md",
}: {
  label: string;
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (value: T) => void;
  size?: "sm" | "md";
}) {
  const group = useId();
  return (
    <div role="radiogroup" aria-label={label} className={clsx(styles.segmented, styles[`seg-${size}`])}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            className={styles.segment}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              const index = options.findIndex((o) => o.value === value);
              const step = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1
                : event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 0;
              if (!step) return;
              event.preventDefault();
              const next = options[(index + step + options.length) % options.length];
              if (next) onChange(next.value);
              const sibling = (event.currentTarget.parentElement?.children[
                (index + step + options.length) % options.length
              ] ?? null) as HTMLElement | null;
              sibling?.focus();
            }}
          >
            {selected ? (
              <m.span
                layoutId={`seg-${group}`}
                className={styles.segmentThumb}
                transition={{ type: "spring", stiffness: 500, damping: 38 }}
              />
            ) : null}
            <span className={styles.segmentLabel}>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// Slider -----------------------------------------------------------------------------------

export function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  format = (v) => String(v),
  hint,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  format?: (value: number) => string;
  hint?: ReactNode;
}) {
  const id = useId();
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  return (
    <div className={styles.slider}>
      <div className={styles.sliderHead}>
        <label htmlFor={id} className={styles.sliderLabel}>
          {label}
        </label>
        <output htmlFor={id} className={styles.sliderValue}>
          {format(value)}
        </output>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-valuetext={format(value)}
        onChange={(event) => onChange(Number(event.target.value))}
        className={styles.range}
        style={{ "--pct": `${pct}%` } as CSSProperties}
      />
      {hint ? <p className={styles.sliderHint}>{hint}</p> : null}
    </div>
  );
}

// Progress ring ----------------------------------------------------------------------------

export function ProgressRing({
  value,
  size = 44,
  stroke = 4,
  label,
}: {
  value: number;
  size?: number;
  stroke?: number;
  label?: string;
}) {
  const gradient = useId();
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, value));
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clamped * 100)}
      aria-label={label}
      className={styles.ring}
    >
      <defs>
        <linearGradient id={gradient} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--iris)" />
          <stop offset="0.55" stopColor="var(--lagoon)" />
          <stop offset="1" stopColor="var(--ember)" />
        </linearGradient>
      </defs>
      <circle cx={size / 2} cy={size / 2} r={radius} className={styles.ringTrack} strokeWidth={stroke} />
      <m.circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={`url(#${gradient})`}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        initial={false}
        animate={{ strokeDashoffset: circumference * (1 - clamped) }}
        transition={{ type: "spring", stiffness: 90, damping: 20 }}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

// Skeleton ---------------------------------------------------------------------------------

export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return <span className={clsx(styles.skeleton, className)} style={style} aria-hidden="true" />;
}
